import { compareDateOnly } from "./dateOnly";
import { parseHoursFromExcel } from "./common";

/** Parse a timesheet line-item hours value to whole minutes (HH:MM, HH.MM, Excel serial, etc.). */
export function parseItemHoursToMinutes(value) {
  if (value == null || value === "") return 0;
  const hhmm = parseHoursFromExcel(value);
  if (!hhmm || !String(hhmm).includes(":")) return 0;
  const [h, m] = String(hhmm)
    .split(":")
    .map((part) => parseInt(part, 10) || 0);
  return h * 60 + m;
}

/** Format whole minutes as H:MM (matches timesheet view / modal). */
export function formatMinutesAsHMM(totalMinutes) {
  const mins = Math.max(0, Math.round(totalMinutes || 0));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** Parse item hours to decimal (for reports/charts). */
export function parseItemHoursToDecimal(value) {
  return parseItemHoursToMinutes(value) / 60;
}

/**
 * Parse report/summary duration to decimal hours.
 * Supports numeric decimals and "H:MM" / "HHH:MM" strings where hours may exceed 24
 * (unlike clock-time parsers used for single-day timesheet line items).
 */
export function parseDurationToDecimalHours(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value >= 0 ? value : 0;
  }
  const s = String(value).trim();
  if (!s) return 0;

  const match = s.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] != null ? Number(match[3]) : 0;
    return hours + minutes / 60 + seconds / 3600;
  }

  const numeric = Number(s);
  if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  return 0;
}

export const normalizeLabel = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Fuzzy match for Excel activity/sub-activity labels (punctuation/spacing). */
export const normalizeLabelForMatch = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();

export const getActivityCanonicalId = (act) => act?.activity_id ?? act?.id;

export const normalizeActivityOption = (act) => {
  const id = getActivityCanonicalId(act);
  return id != null && id !== ""
    ? { ...act, id, activity_id: id }
    : act;
};

export const normalizeSubActivityOption = (sub) => {
  const id = sub?.sub_activity_id ?? sub?.id;
  return id != null && id !== ""
    ? { ...sub, id, sub_activity_id: id }
    : sub;
};

export const normalizeBilledType = (val) =>
  String(val || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s_]/g, "");

/** Minimum total hours required to submit a timesheet (not draft save). */
export const TIMESHEET_MIN_TOTAL_HOURS = 40;

/** Each holiday in the timesheet week reduces the 40-hour minimum by one day. */
export function timesheetMinimumHoursForHolidays(holidayCount) {
  const count = Math.max(0, Number(holidayCount) || 0);
  return Math.max(0, TIMESHEET_MIN_TOTAL_HOURS - count * 8);
}

export function isLeaveBilledType(val) {
  return normalizeBilledType(val) === "leave";
}

/** True when timesheet total hours are below the required minimum. */
export function isTimesheetTotalBelowMinimum(
  totalHoursValue,
  minHours = TIMESHEET_MIN_TOTAL_HOURS,
) {
  // Compare in whole minutes so exact 40:00 is accepted (avoids float drift).
  const s = String(totalHoursValue ?? "").trim();
  const match = s.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
  if (match) {
    const minutes = Number(match[1]) * 60 + Number(match[2]);
    return minutes < minHours * 60;
  }
  return (
    Math.round(parseDurationToDecimalHours(totalHoursValue) * 60) <
    minHours * 60
  );
}

function getActivityMasterBillingType(act) {
  return normalizeBilledType(
    act?.billing_type || act?.billed_type || act?.billingType,
  );
}

/** Match activity labels like "Idle", "Idle time", "Idle Time". */
export function isIdleActivityLabel(label) {
  const compact = normalizeLabelForMatch(label);
  if (!compact) return false;
  return compact === "idle" || compact === "idletime";
}

function isIdleBillingType(val) {
  const norm = normalizeBilledType(val);
  if (!norm) return false;
  return norm === "idle" || norm === "idletime";
}

/** Resolve activity master row for a timesheet line item (by id, then label). */
export function findActivityMasterEntry(item, activityList = []) {
  if (!Array.isArray(activityList) || !activityList.length) return null;

  const activityId = item?.activity_id;
  if (
    activityId != null &&
    activityId !== "" &&
    activityId !== "Others" &&
    activityId !== 0 &&
    activityId !== "0"
  ) {
    const byId = activityList.find(
      (act) => String(getActivityCanonicalId(act)) === String(activityId),
    );
    if (byId) return byId;
  }

  const label = normalizeLabel(item?.activity || item?.value || "");
  if (!label) return null;

  return (
    activityList.find((act) => {
      const actLabel = normalizeLabel(act?.activity || act?.value || "");
      return actLabel === label;
    }) || null
  );
}

/** Idle is determined by activity name (e.g. "Idle time") or activity master billing type. */
export function isIdleTimesheetItem(item, activityList = []) {
  const activityLabel = item?.activity || item?.value || "";
  if (isIdleActivityLabel(activityLabel)) {
    return true;
  }

  const master = findActivityMasterEntry(item, activityList);
  if (!master) return false;

  if (isIdleBillingType(getActivityMasterBillingType(master))) {
    return true;
  }

  return isIdleActivityLabel(master?.activity || master?.value || "");
}

/** Split line-item minutes into billable / non-billable / idle buckets. */
export function classifyTimesheetItemMinutes(item, activityList = []) {
  const minutes = parseItemHoursToMinutes(item?.hours);
  if (!minutes) {
    return { billable: 0, nonBillable: 0, idle: 0 };
  }

  if (isIdleTimesheetItem(item, activityList)) {
    return { billable: 0, nonBillable: 0, idle: minutes };
  }

  const billedNorm = normalizeBilledType(
    item?.billed_type || item?.billing_type || item?.billingType,
  );
  if (billedNorm === "nonbillable" || billedNorm === "leave") {
    return { billable: 0, nonBillable: minutes, idle: 0 };
  }

  return { billable: minutes, nonBillable: 0, idle: 0 };
}

export const resolveSubActivityDisplay = (item) => {
  const sub = String(item?.sub_activity || item?.subactivity || "").trim();
  const custom = String(item?.custom_sub_activity || "").trim();
  return sub || custom || "";
};

export const resolveSubActivitySelectId = (entry, subs) => {
  const normalized = (subs || []).map(normalizeSubActivityOption);
  const subId = entry?.sub_activity_id;
  const subLabel = resolveSubActivityDisplay(entry);
  if (subId != null && subId !== "") {
    const byId = normalized.find(
      (s) => String(s.sub_activity_id) === String(subId),
    );
    if (byId) return String(byId.sub_activity_id);
  }
  if (subLabel) {
    const key = normalizeLabel(subLabel);
    const byLabel = normalized.find(
      (s) => normalizeLabel(s.sub_activity || s.value) === key,
    );
    if (byLabel) return String(byLabel.sub_activity_id);
  }
  return subId != null && subId !== "" ? String(subId) : "";
};

export const resolveSubActivityEditValue = (entry, subs) => {
  const display = resolveSubActivityDisplay(entry);
  if (!display) return "";

  const resolvedId = resolveSubActivitySelectId(entry, subs);
  if (resolvedId) return resolvedId;

  return display;
};

export const isSubActivityTextMode = (item, subs = []) => {
  if (!item) return false;
  if (normalizeBilledType(item.billed_type) === "nonbillable") return true;
  if (!item.activity_id && item.custom_sub_activity) return true;
  if (item.activity_id === "Others") return true;

  const custom = String(item?.custom_sub_activity || "").trim();
  if (custom && !resolveSubActivitySelectId(item, subs)) return true;

  return false;
};

/** Timesheet line items: oldest date first, then item id for stable order. */
export function sortTimesheetItemsByDateAsc(items) {
  if (!Array.isArray(items) || items.length < 2) {
    return items ? [...items] : [];
  }
  return [...items].sort((a, b) => {
    const byDate = compareDateOnly(a?.date, b?.date);
    if (byDate !== 0) return byDate;
    const idA = a?.timesheet_item_id;
    const idB = b?.timesheet_item_id;
    if (idA && idB) {
      return String(idA).localeCompare(String(idB));
    }
    return 0;
  });
}
